import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { GuidanceProvider, UserSettings } from './types';

const STORAGE_KEY = 'prayer-companion-settings';

const defaultSettings: UserSettings = {
  guidanceProvider: 'openai',
};

interface SettingsContextType {
  settings: UserSettings;
  setGuidanceProvider: (provider: GuidanceProvider) => Promise<void>;
  loading: boolean;
}

const SettingsContext = createContext<SettingsContextType>({
  settings: defaultSettings,
  setGuidanceProvider: async () => {},
  loading: false,
});

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);

  // Load settings from Supabase profile or localStorage
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      
      if (!user) {
        // For anonymous users, use localStorage
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            const parsedSettings = JSON.parse(stored);
            if (parsedSettings.provider) {
              setSettings({ guidanceProvider: parsedSettings.provider });
            }
          }
        } catch (error) {
          console.error('Failed to load settings from localStorage:', error);
        }
        setSettings(defaultSettings);
        setLoading(false);
        return;
      }

      // For authenticated users, load from Supabase
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('guidance_provider')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Failed to load settings from Supabase:', error.message);
          // Fallback to localStorage for migration
          await migrateFromLocalStorage();
        } else if (data?.guidance_provider) {
          setSettings({ guidanceProvider: data.guidance_provider as GuidanceProvider });
        } else {
          // No setting in DB yet, check localStorage for migration
          await migrateFromLocalStorage();
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSettings(defaultSettings);
      }
      
      setLoading(false);
    };

    const migrateFromLocalStorage = async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsedSettings = JSON.parse(stored);
          const provider = parsedSettings.provider as GuidanceProvider;
          if (provider && ['openai', 'perplexity', 'hybrid'].includes(provider)) {
            // Migrate to Supabase
            await supabase
              .from('profiles')
              .update({ guidance_provider: provider })
              .eq('id', user!.id);
            
            setSettings({ guidanceProvider: provider });
            // Clear localStorage after successful migration
            localStorage.removeItem(STORAGE_KEY);
            return;
          }
        }
      } catch (error) {
        console.error('Migration from localStorage failed:', error);
      }
      
      // If migration fails or no localStorage data, use default
      setSettings(defaultSettings);
    };

    loadSettings();
  }, [user]);

  // Update both Supabase and local state
  const setGuidanceProvider = async (provider: GuidanceProvider) => {
    setSettings({ guidanceProvider: provider });

    if (user) {
      // Authenticated user: save to Supabase
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ guidance_provider: provider })
          .eq('id', user.id);

        if (error) {
          console.error('Failed to update guidanceProvider in Supabase:', error.message);
          // Fallback to localStorage
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ provider }));
        }
      } catch (error) {
        console.error('Error updating settings:', error);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ provider }));
      }
    } else {
      // Anonymous user: save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ provider }));
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, setGuidanceProvider, loading }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};